import React, { useCallback, useEffect, useMemo, useState } from "react";
import Container from "@mui/material/Container";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider as MuiThemeProvider, createTheme } from "@mui/material/styles";
import styled, {
  ThemeProvider as StyledThemeProvider,
} from "styled-components";
import FourOFour from "./pages/404";
import FourOOne from "./pages/unauthorized";
import Sidebar from "./components/sidebar";
import routes, { routeAllowed } from "./pages";
import useUserData from "./hooks/useUserData";
import MyProfile from "./components/MyProfile";
import UserSync from "./components/UserSync";
import { lightTheme as light, darkTheme as dark } from "./utils/theme.js";
import "./App.css";
import useMemorize from "./hooks/useMemorize.js";
import useLanguage from "./hooks/useLanguage.js";
import { Box } from "@mui/material";
import { DrawerProvider } from "./hooks/useDrawer.js";
import { ToasterProvider } from "./hooks/useToaster.js";
import Login from "./pages/login/index.jsx";
import useMediaQuery from "./hooks/useMediaQuery.js";
import {
  requestAuthFromParent,
  notifyParentLoginSuccess,
  notifyParentLoginFailed,
} from "./utils/notifyParent.js";

const baseUrl = process.env.REACT_APP_API_URL || "";

async function hydrateFromToken(token, userdata = null) {
  // Use parent-provided userdata when it's complete (Reshet's AUTH_TOKEN push
  // includes the full user object). Fall back to /api/me when missing or
  // missing the fields we need, so the spec-compliant handshake path still
  // works the same.
  let user = userdata && userdata.id ? userdata : null;
  if (!user) {
    const res = await fetch(`${baseUrl}/api/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return false;
    user = await res.json();
  }
  const initials = (user.name || "User")
    .split(" ")
    .map((n) => n[0])
    .join("");
  window.localStorage.setItem("x-token", token);
  window.localStorage.setItem(
    "userdata",
    JSON.stringify({
      id: user.id,
      name: user.name,
      email: user.email,
      initials,
      nickname: user.name,
      color: "#4CAF50",
      role: user.role ?? "user",
      active: user.active ?? true,
      group: user.group ?? null,
    })
  );
  return true;
}

const MainContainer = styled(Container)`
  background-color: ${({ theme }) => theme.palette?.background?.secondary};
  display: flex;
  align-items: center;
  height: 100svh; // Or dvh???
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  padding: 0;
  padding-inline-end: 7px;
  margin: 0;

  &.rtl,
  &.rtl * {
    direction: rtl;
  }
  &.rtl {
    text-align: right;
  }
`;

const ContentContainer = styled(Box)`
  background-color: ${({ theme }) => theme.palette?.background?.default};
  border: 1px solid ${({ theme }) => theme.palette?.borderColor?.default};
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  border-radius: 12px;
  overflow-y: auto;
  overflow-x: hidden;
`;

const Drawer = styled(Box)`
  height: 100%;
  max-width: 0px;
  min-width: 0px;
  transition: max-width 0.3s ease;
  transition: min-width 0.3s ease;
  overflow-x: hidden;
  overflow-y: auto;
  text-overflow: ellipsis;

  &.open {
    min-width: 400px;

    &.mobile {
      min-width: calc(100vw - 45px) !important;
    }
  }
`;

const specialRoutes = {
  "/changelog": {
    component: FourOFour,
  },
  404: {
    component: FourOFour,
  },
};

const handleChangingComponent = (route) =>
  specialRoutes[route] ||
  routes.find(
    (r) => r.route === route || (r.route !== "/" && route.startsWith(r.route))
  ) ||
  specialRoutes[404];

function App() {
  const isMobile = useMediaQuery();
  const [component, setComponent] = useState(
    handleChangingComponent(window.location.pathname)
  );
  const [isOpen, open] = useState(false);
  const [Element, changeElement] = useState(null);
  const [login, setLogin] = useState(window.localStorage.getItem("x-token"));
  const [bootstrapping, setBootstrapping] = useState(
    !window.localStorage.getItem("x-token") && window.parent !== window
  );
  const [profileOpen, setProfileOpen] = useState(false);
  const [theme, setTheme] = useMemorize("theme");
  const [{ direction }] = useLanguage();
  const userData = useUserData();
  const componentAllowed = routeAllowed(component, userData?.role);
  const baseTheme = useMemo(() => (theme === "dark" ? dark : light), [theme]);
  const getTheme = useMemo(
    () => createTheme(baseTheme, { direction: direction === "rtl" ? "rtl" : "ltr" }),
    [baseTheme, direction]
  );
  const lazySetComponent = useCallback((route) => setComponent(route), []);

  const setElement = useCallback(
    async (element) => {
      await changeElement(<></>);
      changeElement(element);
    },
    [open]
  );

  useEffect(() => {
    changeElement(null);
    open(false);
  }, [component]);

  useEffect(() => {
    const handleRouteChange = () => {
      setComponent(handleChangingComponent(window.location.pathname));
    };

    window.addEventListener("popstate", handleRouteChange);
    return () => window.removeEventListener("popstate", handleRouteChange);
  }, []);

  useEffect(() => {
    if (!bootstrapping) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await requestAuthFromParent();
        if (cancelled) return;
        if (result?.token && (await hydrateFromToken(result.token, result.userdata))) {
          setLogin(result.token);
          notifyParentLoginSuccess();
        } else {
          // Auto-login didn't complete: either no usable token arrived in time,
          // or the parent's token was rejected. Tell the embedder so its loader
          // drops instead of hanging.
          notifyParentLoginFailed(result?.token ? "invalid_token" : "no_token");
        }
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bootstrapping]);

  // Central logout: any 401 from the API (or a failed boot validation below)
  // dispatches "auth-logout". Clear the session and flip React state so <Login>
  // renders. The old per-hook pushState("/login") never reset `login`, so the
  // app stayed mounted on a dead token and rendered a broken shell. When embedded
  // we re-ask the parent for a fresh token instead of showing our login screen.
  useEffect(() => {
    const onLogout = () => {
      window.localStorage.removeItem("x-token");
      window.localStorage.removeItem("userdata");
      setLogin(null);
      if (window.parent !== window) setBootstrapping(true);
    };
    window.addEventListener("auth-logout", onLogout);
    return () => window.removeEventListener("auth-logout", onLogout);
  }, []);

  // A stored token that's stale/expired must NOT count as logged in. Validate it
  // on boot; a 401 flips the UI to the login gate via auth-logout. The optimistic
  // render (login seeded from localStorage) stays instant for valid tokens.
  useEffect(() => {
    const token = window.localStorage.getItem("x-token");
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${baseUrl}/api/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (res.status === 401) {
          window.dispatchEvent(new Event("auth-logout"));
        } else if (res.ok) {
          // Warm reload with an already-valid session: no handshake runs, so the
          // embedder's login ack would otherwise never fire and its loader would
          // hang. Confirm the live session to the parent here too.
          notifyParentLoginSuccess();
        }
      } catch {
        // Network/proxy error — keep the optimistic render; real calls will retry.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <MuiThemeProvider theme={getTheme}>
      <StyledThemeProvider theme={getTheme}>
        <CssBaseline />
        <ToasterProvider>
          <DrawerProvider value={{ open, setElement }}>
            {login && <UserSync />}
            {login ? (
              <MainContainer
                maxWidth={false}
                disableGutters
                className={`App ${direction}`}
              >
                <Sidebar
                  routes={routes}
                  specialRoutes={specialRoutes}
                  setComponent={lazySetComponent}
                  setTheme={setTheme}
                  onOpenProfile={() => setProfileOpen(true)}
                  onLogout={() => {
                    window.localStorage.removeItem("x-token");
                    window.localStorage.removeItem("userdata");
                    setLogin(null);
                  }}
                />
                <ContentContainer>
                  {!componentAllowed ? (
                    <FourOOne />
                  ) : !!component.component ? (
                    <component.component />
                  ) : (
                    <FourOFour />
                  )}
                </ContentContainer>
                <MyProfile
                  open={profileOpen}
                  onClose={() => setProfileOpen(false)}
                />
                <Drawer
                  className={`${isOpen ? "open" : ""} ${isMobile ? "mobile" : ""
                    }`}
                >
                  {isOpen && Element && Element}
                </Drawer>
              </MainContainer>
            ) : bootstrapping ? null : (
              <Login setLogin={setLogin} />
            )}
          </DrawerProvider>
        </ToasterProvider>
      </StyledThemeProvider>
    </MuiThemeProvider>
  );
}

export default App;
